/**
 * Single source of truth for Tag Type chrome (emoji icon, swatch colour,
 * i18n label key). Shared between the Tag Templates list page
 * (``/tag-templates``) and the Tag Template form (``/template-form``) so
 * the icon a user picks in the form dropdown matches what the list
 * renders for the saved row.
 *
 * Backend enum lives in
 * ``backend/app/models/tag.py::TagTypeEnum`` — keep this map in sync.
 */
import type { TagTypeEnum } from "@/client"

// Chakra v3's badge ``colorPalette`` and the codebase's Badge wrapper's
// ``colorScheme`` accept the same closed string union. Keep the field
// narrowed so consumers don't have to cast.
export type TagTypeColor =
  | "blue"
  | "cyan"
  | "gray"
  | "green"
  | "orange"
  | "pink"
  | "purple"
  | "red"
  | "teal"
  | "yellow"

export interface TagTypeMeta {
  icon: string
  color: TagTypeColor
  labelKey: string
}

// Default applied when an enum value reaches the list page from a
// template that was created before a new tag-type was added — we never
// want to render a blank cell.
const FALLBACK: TagTypeMeta = {
  icon: "📡",
  color: "gray",
  labelKey: "tags:types.GENERIC",
}

const META: Record<TagTypeEnum, TagTypeMeta> = {
  TEMPERATURE: {
    icon: "🌡️",
    color: "red",
    labelKey: "tags:types.TEMPERATURE",
  },
  TEMPERATURE_DATALOGGER: {
    icon: "📈",
    color: "orange",
    labelKey: "tags:types.TEMPERATURE_DATALOGGER",
  },
  SENSOR_TAG: {
    icon: "🛰️",
    color: "purple",
    labelKey: "tags:types.SENSOR_TAG",
  },
  HUMIDITY: {
    icon: "💧",
    color: "blue",
    labelKey: "tags:types.HUMIDITY",
  },
  NFC: {
    icon: "📲",
    color: "teal",
    labelKey: "tags:types.NFC",
  },
  BARCODE: {
    icon: "📦",
    color: "yellow",
    labelKey: "tags:types.BARCODE",
  },
  MULTIMODAL: {
    icon: "🧩",
    color: "pink",
    labelKey: "tags:types.MULTIMODAL",
  },
  GENERIC: {
    icon: "📡",
    color: "gray",
    labelKey: "tags:types.GENERIC",
  },
}

export function getTagTypeMeta(
  type: TagTypeEnum | string | undefined | null,
): TagTypeMeta {
  if (!type) return FALLBACK
  return META[type as TagTypeEnum] ?? FALLBACK
}

// Ordered list for dropdown rendering — TEMPERATURE first because it's
// the most common selection in this codebase's demo seed and example
// templates.
export const TAG_TYPE_ORDER: TagTypeEnum[] = [
  "TEMPERATURE",
  "TEMPERATURE_DATALOGGER",
  "SENSOR_TAG",
  "HUMIDITY",
  "NFC",
  "BARCODE",
  "MULTIMODAL",
  "GENERIC",
]

export interface TagTypeOption {
  value: TagTypeEnum
  meta: TagTypeMeta
}

export const TAG_TYPE_OPTIONS: TagTypeOption[] = TAG_TYPE_ORDER.map(
  (value) => ({
    value,
    meta: META[value],
  }),
)
